import * as React from 'react';
import { motion } from 'motion/react';
import { Split, ExternalLink, ArrowUpRight, Loader2, AlertTriangle, Building2, CalendarClock, Info } from 'lucide-react';
import { cn } from '../lib/utils';
import { auth } from '../firebase';
import { Tooltip } from './Tooltip';

interface SpinoffCard {
  id: string;
  cik: string;
  spincoName: string | null;
  parentName: string | null;
  parentTicker: string | null;
  parentCik: string | null;
  ticker: string | null;
  status: string;
  formType: string | null;
  filingDate: string | null;
  filingUrl: string | null;
  distributionDate: string | null;
  distributionRatio: string | null;
  expectedDate: string | null;
  parentResolutionConfidence: string | null;
}

interface IpoCard {
  id: string;
  ticker: string | null;
  spincoName: string | null;
  ipoDate: string | null;
  exchange: string | null;
  priceRange: string | null;
  status: string;
}

interface SpinoffListResponse {
  success: boolean;
  aboutToSpinOff: SpinoffCard[];
  recentSpinoffs: SpinoffCard[];
  recentIpos: IpoCard[];
  counts: { aboutToSpinOff: number; recentSpinoffs: number; recentIpos: number };
}

interface SpinoffIntelligenceProps {
  onAnalyze: (ticker: string) => void;
}

export const SpinoffIntelligence: React.FC<SpinoffIntelligenceProps> = ({ onAnalyze }) => {
  const [data, setData] = React.useState<SpinoffListResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [subTab, setSubTab] = React.useState<'spinoffs' | 'ipos'>('spinoffs');

  React.useEffect(() => {
    fetch('/api/telemetry', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: 'new_listings_subtab_viewed', properties: { subTab } }),
    }).catch(() => {});
  }, [subTab]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await fetch('/api/spinoff/list', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`Failed to load spin-off data (${res.status})`);
        const json = (await res.json()) as SpinoffListResponse;
        if (!cancelled) setData(json);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load spin-off data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fmtDate = (s: string | null) => (s ? s : '—');

  const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const map: Record<string, string> = {
      registered: 'text-warning-700 bg-warning-50 border-warning-100',
      effective_unconfirmed: 'text-warning-700 bg-warning-50 border-warning-100',
      announced: 'text-ink-600 bg-surface-2 border-ink-200',
      trading: 'text-success-700 bg-success-50 border-success-100',
      ipo_expected: 'text-warning-700 bg-warning-50 border-warning-100',
      ipo_listed: 'text-success-700 bg-success-50 border-success-100',
    };
    const label: Record<string, string> = {
      registered: 'Form 10 Filed',
      effective_unconfirmed: 'Awaiting Confirmation',
      announced: 'Announced',
      trading: 'Trading',
      ipo_expected: 'IPO Expected',
      ipo_listed: 'Trading',
    };
    return (
      <span className={cn('inline-flex items-center px-2.5 py-1 rounded-md border text-[10px] font-medium uppercase tracking-widest', map[status] || 'text-ink-600 bg-surface-2 border-ink-200')}>
        {label[status] || status}
      </span>
    );
  };

  const IpoCardComponent: React.FC<{ c: IpoCard }> = ({ c }) => (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="oi-card p-5 flex flex-col gap-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="font-serif text-lg text-ink leading-snug truncate">{c.spincoName || 'Unnamed IPO'}</h4>
          <div className="mt-1 flex items-center gap-2 text-xs text-ink-500">
            <Building2 className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">
              {c.exchange ? `${c.exchange}` : 'Exchange pending'}
            </span>
            {c.priceRange && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-surface text-ink-600 border border-ink-200">
                {c.priceRange}
              </span>
            )}
          </div>
        </div>
        <StatusBadge status={c.status} />
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
        <div><span className="text-ink-500">IPO Date</span><div className="font-mono text-ink">{fmtDate(c.ipoDate)}</div></div>
        <div><span className="text-ink-500">Ticker</span><div className="font-mono text-ink">{c.ticker || 'pending'}</div></div>
      </div>

      <div className="text-[11px] text-ink-500 italic mt-2">
        Most recent IPOs lack the history for a full valuation — expect PASS verdicts with qualitative analysis.
      </div>

      <div className="flex items-center justify-between gap-3 pt-1">
        <span />

        {c.ticker ? (
          <button
            type="button"
            onClick={() => onAnalyze(c.ticker as string)}
            className="inline-flex items-center gap-2 bg-oxblood text-paper text-xs font-medium px-4 py-2 rounded-md hover:bg-oxblood-800 transition-all active:scale-95"
          >
            <span>Run Oracle Analysis</span>
            <ArrowUpRight className="w-3.5 h-3.5" />
          </button>
        ) : (
          <span className="text-[10px] uppercase tracking-widest text-ink-500">Ticker pending</span>
        )}
      </div>
    </motion.div>
  );

  const Card: React.FC<{ c: SpinoffCard; recent?: boolean }> = ({ c, recent }) => (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="oi-card p-5 flex flex-col gap-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="font-serif text-lg text-ink leading-snug truncate">{c.spincoName || 'Unnamed spin-off'}</h4>
          <div className="mt-1 flex items-center gap-2 text-xs text-ink-500">
            <Building2 className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">
              from {c.parentName || 'unknown parent'}
              {c.parentTicker ? ` (${c.parentTicker})` : ''}
            </span>
            {c.parentResolutionConfidence && c.parentResolutionConfidence !== 'exact' && c.parentResolutionConfidence !== 'strong' && (
              <Tooltip content="Parent identity not yet confirmed against the distribution 8-K.">
                <Info className="w-3.5 h-3.5 text-warning-600" />
              </Tooltip>
            )}
          </div>
        </div>
        <StatusBadge status={c.status} />
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
        {recent ? (
          <>
            <div><span className="text-ink-500">Distribution</span><div className="font-mono text-ink">{fmtDate(c.distributionDate)}</div></div>
            <div><span className="text-ink-500">Ticker</span><div className="font-mono text-ink">{c.ticker || 'pending'}</div></div>
          </>
        ) : (
          <>
            <div><span className="text-ink-500">Filed</span><div className="font-mono text-ink">{fmtDate(c.filingDate)} {c.formType ? `· ${c.formType}` : ''}</div></div>
            <div><span className="text-ink-500">Expected</span><div className="font-mono text-ink">{fmtDate(c.expectedDate)}</div></div>
          </>
        )}
        {c.distributionRatio && (
          <div className="col-span-2"><span className="text-ink-500">Ratio</span><div className="text-ink">{c.distributionRatio}</div></div>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 pt-1">
        {c.filingUrl ? (
          <a href={c.filingUrl} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1.5 text-xs text-ink-500 hover:text-oxblood transition-colors">
            <ExternalLink className="w-3.5 h-3.5" /> SEC Filing
          </a>
        ) : <span />}

        {recent && c.ticker ? (
          <button
            type="button"
            onClick={() => onAnalyze(c.ticker as string)}
            className="inline-flex items-center gap-2 bg-oxblood text-paper text-xs font-medium px-4 py-2 rounded-md hover:bg-oxblood-800 transition-all active:scale-95"
          >
            <span>Run Oracle Analysis</span>
            <ArrowUpRight className="w-3.5 h-3.5" />
          </button>
        ) : recent ? (
          <span className="text-[10px] uppercase tracking-widest text-ink-500">Ticker pending</span>
        ) : (
          <span className="text-[10px] uppercase tracking-widest text-ink-500">Not yet trading</span>
        )}
      </div>
    </motion.div>
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="oi-card p-5 md:p-10">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-oxblood-50 border border-oxblood/20 text-[10px] font-medium uppercase tracking-widest text-oxblood mb-6">
            <Split className="w-3 h-3" />
            <span>Corporate Separation Signal</span>
          </div>
          <h2 className="text-4xl font-serif font-medium mb-4 text-ink">
            New Listings <span className="italic">Intelligence</span>
          </h2>
          <p className="text-ink-500 text-lg">
            Companies registering to spin off or recent IPOs. Detected daily from SEC filings.
          </p>
        </div>
      </div>

      {error && (
        <div className="p-6 border rounded-lg flex items-center gap-4 bg-danger-50 border-danger-100 text-danger-600">
          <AlertTriangle className="w-6 h-6 shrink-0" />
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}

      {loading && (
        <div className="py-20 flex flex-col items-center justify-center gap-4">
          <Loader2 className="w-8 h-8 text-oxblood animate-spin" />
          <p className="text-xs text-ink-500 uppercase tracking-widest font-medium">Loading spin-off registry…</p>
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* Sub-tab toggle */}
          <div className="flex justify-center">
            <div className="bg-surface-2 p-1 rounded-md inline-flex">
              <button
                onClick={() => setSubTab('spinoffs')}
                className={`px-6 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                  subTab === 'spinoffs' ? 'bg-surface text-ink border border-ink-200' : 'text-ink-500 hover:text-ink'
                }`}
              >
                Spin-Offs <span className="ml-1 font-mono text-xs text-ink-500">({data.counts.recentSpinoffs + data.counts.aboutToSpinOff})</span>
              </button>
              <button
                onClick={() => setSubTab('ipos')}
                className={`px-6 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                  subTab === 'ipos' ? 'bg-surface text-ink border border-ink-200' : 'text-ink-500 hover:text-ink'
                }`}
              >
                IPOs <span className="ml-1 font-mono text-xs text-ink-500">({data.counts.recentIpos})</span>
              </button>
            </div>
          </div>

          {subTab === 'spinoffs' && (
            <>
              {/* Recent Spin-Offs */}
              <section className="space-y-4">
                <div className="flex items-center gap-3">
                  <CalendarClock className="w-4 h-4 text-oxblood" />
                  <h3 className="font-serif text-xl text-ink">Recent Spin-Offs</h3>
                  <span className="text-[10px] font-mono text-ink-500">({data.counts.recentSpinoffs})</span>
                  <span className="text-xs text-ink-500">· trading, within ~365 days of distribution</span>
                </div>
                {data.recentSpinoffs.length === 0 ? (
                  <div className="oi-card p-6 text-sm text-ink-500">No spin-offs have begun trading inside the window yet. Newly-registered separations appear below until their distribution completes.</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {data.recentSpinoffs.map((c) => <Card key={c.id} c={c} recent />)}
                  </div>
                )}
              </section>

              {/* About to Spin Off */}
              <section className="space-y-4">
                <div className="flex items-center gap-3">
                  <Split className="w-4 h-4 text-ink-600" />
                  <h3 className="font-serif text-xl text-ink">About to Spin Off</h3>
                  <span className="text-[10px] font-mono text-ink-500">({data.counts.aboutToSpinOff})</span>
                  <span className="text-xs text-ink-500">· registered, not yet trading</span>
                </div>
                {data.aboutToSpinOff.length === 0 ? (
                  <div className="oi-card p-6 text-sm text-ink-500">No pending spin-off registrations detected in the current window.</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {data.aboutToSpinOff.map((c) => <Card key={c.id} c={c} />)}
                  </div>
                )}
              </section>
            </>
          )}

          {subTab === 'ipos' && (
            <>
              {/* Recent IPOs */}
              <section className="space-y-4">
                <div className="flex items-center gap-3">
                  <CalendarClock className="w-4 h-4 text-oxblood" />
                  <h3 className="font-serif text-xl text-ink">Recent IPOs</h3>
                  <span className="text-[10px] font-mono text-ink-500">({data.counts.recentIpos})</span>
                  <span className="text-xs text-ink-500">· expected or listed within ~365 days</span>
                </div>
                {data.recentIpos.length === 0 ? (
                  <div className="oi-card p-6 text-sm text-ink-500">No recent IPOs surfaced yet.</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {data.recentIpos.map((c) => <IpoCardComponent key={c.id} c={c} />)}
                  </div>
                )}
              </section>
            </>
          )}
        </>
      )}
    </div>
  );
};
